import { IAuthenticatedAccount } from '@alberta/konexi-shared';

/**
 * Validation of the authenticated account object (#26615, incident 24139).
 *
 * The account is required for far more than "is someone logged in": rights, regions, the tenant
 * and even the name of the local databases are derived from it
 * (`DatabaseFactory.createDbName()`). An account that misses one of these fields is unusable, and
 * writing it over the last known good copy in storage turns a temporary backend glitch into a
 * forced logout on the next offline start.
 *
 * The predicate is intentionally identical to the two inline checks it replaces, and the reason
 * strings keep their original wording (including the caller's label) so existing Sentry issues
 * and alerts keep grouping the same way.
 */
export interface IAccountValidationResult {
  isValid: boolean;
  /** Human readable reason, `undefined` when valid. Same wording as before the extraction. */
  reason?: string;
}

const VALID: IAccountValidationResult = { isValid: true };

/**
 * @param account the value to check
 * @param label how the value is called in the caller's log message (`user`, `currentUser`, …)
 */
export function validateAccount(account: unknown, label: string): IAccountValidationResult {
  if (!account) {
    return { isValid: false, reason: `${label} is null/undefined` };
  }
  if (typeof account !== 'object') {
    return { isValid: false, reason: `${label} is not an object (type: ${typeof account})` };
  }

  const candidate = account as Partial<IAuthenticatedAccount>;
  if (!candidate._id) {
    return { isValid: false, reason: `${label}._id is missing` };
  }
  if (!candidate.authorization) {
    return { isValid: false, reason: `${label}.authorization is missing` };
  }
  if (!candidate.authorization.hydratedOrganization) {
    return { isValid: false, reason: `${label}.authorization.hydratedOrganization is missing` };
  }

  return VALID;
}

/** Convenience wrapper for call sites that only need the boolean. */
export function isValidAccount(account: unknown): boolean {
  return validateAccount(account, 'account').isValid;
}

/**
 * Reads the `_id` of a possible account without ever throwing — for log messages about values
 * that are suspected to be broken.
 */
export function readAccountId(account: unknown): string | null {
  if (!account || typeof account !== 'object') {
    return null;
  }
  const id = (account as Partial<IAuthenticatedAccount>)._id;
  return typeof id === 'string' ? id : null;
}
